import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useConfig } from './useConfig';

const SocketContext = createContext(null);

/**
 * A custom hook that allows any component to easily access the shared Socket.IO connection.
 * @returns {object|null} The socket instance, or null if not yet connected.
 */
export const useSocket = () => {
    return useContext(SocketContext);
};

/**
 * This provider component is responsible for creating a single, shared Socket.IO connection
 * for the entire application. It waits for the server configuration to be loaded, then uses
 * the correct server URL to connect.
 */
export const SocketProvider = ({ children }) => {
    const { config } = useConfig();
    const [socket, setSocket] = useState(null);

    useEffect(() => {
        // This effect will run only when the application has successfully loaded the configuration.
        if (config?.base_url) {
            // Establish the connection to the server using the URL from the config.
            const newSocket = io(config.base_url);
            setSocket(newSocket);

            // This is a cleanup function that runs when the application closes.
            // It ensures that the socket connection is properly disconnected to prevent memory leaks.
            return () => newSocket.close();
        }
    }, [config]); // This effect depends on the 'config' object being loaded.

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};
